import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminApiError } from '../../api/admin-client.js';
import {
  fetchAdminPayments,
  fetchAdminPaymentDetail,
  initiateAdminRefund,
  fetchAdminRefunds,
  fetchAdminRefundDetail,
} from '../api/payments-api.js';
import type { PaymentListParams, RefundListParams, InitiateRefundInput } from '../types/payments.js';

const PAYMENTS_KEY = ['admin', 'payments'] as const;
const REFUNDS_KEY = ['admin', 'refunds'] as const;

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new AdminApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

function nonRetryableStatus(error: unknown): boolean {
  return error instanceof AdminApiError && [401, 403, 404, 422].includes(error.status);
}

export function useAdminPayments(params: PaymentListParams) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: [...PAYMENTS_KEY, 'list', params] as const,
    queryFn: async ({ signal }) => fetchAdminPayments(params, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 15_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

export function useAdminPaymentDetail(paymentId: string | undefined) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: [...PAYMENTS_KEY, 'detail', paymentId ?? ''] as const,
    queryFn: async ({ signal }) =>
      fetchAdminPaymentDetail(paymentId!, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true && Boolean(paymentId),
    staleTime: 10_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

export function useInitiateRefund() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ paymentId, input }: { paymentId: string; input: InitiateRefundInput }) =>
      initiateAdminRefund(paymentId, input, { token: await requireToken(getToken) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
      void queryClient.invalidateQueries({ queryKey: REFUNDS_KEY });
    },
  });
}

export function useAdminRefunds(params: RefundListParams) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: [...REFUNDS_KEY, 'list', params] as const,
    queryFn: async ({ signal }) => fetchAdminRefunds(params, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 15_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

export function useAdminRefundDetail(refundId: string | undefined) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: [...REFUNDS_KEY, 'detail', refundId ?? ''] as const,
    queryFn: async ({ signal }) =>
      fetchAdminRefundDetail(refundId!, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true && Boolean(refundId),
    staleTime: 10_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}
